import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { Link, useLocation } from 'react-router-dom'
import { UserAvatar } from '~/components/user-avatar/user-avatar.component'
import { Tooltip, makeStyles, IconButton, Fade, useTheme } from '@material-ui/core'
import { Check } from '@material-ui/icons'
import { CopySvg } from '~/components/icons'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { PATHS } from '~/common/constants'
import { candidateDetailPath } from '~/features/recruiter-v2/shell/maestro-version.helper'

const WrapperBox = styled.div`
	cursor: pointer;
	display: flex;
	width: 100%;
	min-width: 0;
	overflow: hidden;
	align-items: center;
`

const TitleLink = styled(Link)`
	margin-left: 10px;
	min-width: 0;
	overflow: hidden;
	color: ${({ color }) => color || 'inherit'};
	text-decoration: none;
	&:hover {
		text-decoration: ${({ disableClick }) => (disableClick ? 'none' : 'underline')};
	}
`

/**
 * One shared tooltip for every name cell. MUI gives each row its own Tooltip,
 * and this table positions rows with `transform`, which does not fire
 * `mouseleave` when a row slides out from under the pointer. The previous
 * tooltip then stays open, and `placement="bottom-start"` paints it on top of
 * the next candidate — the dark name/email chip in the screenshot.
 */
const TOOLTIP_DELAY = 500
let openTooltipId = 0
let tooltipSerial = 0
let tooltipGeneration = 0
const tooltipSubscribers = new Set()
let scrollCloseInstalled = false

const publishTooltip = (id) => {
	if (openTooltipId === id) return
	openTooltipId = id
	tooltipSubscribers.forEach((notify) => notify(id))
}

const installTooltipScrollClose = () => {
	if (scrollCloseInstalled || typeof window === 'undefined') return
	scrollCloseInstalled = true
	// Scrolling moves a transformed row without a mouseleave. Drop the chip,
	// and cancel a chip that was still waiting out its delay.
	window.addEventListener(
		'scroll',
		() => {
			tooltipGeneration += 1
			publishTooltip(0)
		},
		true
	)
}

const overflows = (node) => Boolean(node && node.scrollWidth > node.clientWidth + 1)

const useStyles = makeStyles((theme) => ({
	title: {
		...theme.typography.title1,
		width: '100%',
		minWidth: 0,
		overflow: 'hidden',
	},
	fullName: {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		maxWidth: '100%',
	},
	email: {
		...theme.typography.body1,
		color: '#333335',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		flex: '0 1 auto', // Allow email to shrink but don't grow
		minWidth: 0, // Allow text to shrink below content size
	},
	emailContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: '2px',
		width: 'fit-content',
		maxWidth: '100%',
		minWidth: 0, // Allow flex item to shrink
	},
	person: {
		'&:hover $fullName': {
			textDecoration: 'underline',
		},
	},
}))

export const DEFAULT_AVATAR = process.env.PUBLIC_URL + '/assets/care/default-worker-avatar.png?version=' + process.env.REACT_APP_CI_COMMIT_TAG

export const UserTitle = ({ disableClick, data, color, useForJobApplicant = false, jobApplicant, onClick, linkTo, ...rest }) => {
	const classes = useStyles()
	const theme = useTheme()
	const location = useLocation()
	const { t } = useTranslation()
	// `linkTo` lets a table that lists something other than candidates — the User
	// Roles table lists internal users — reuse this cell without inheriting the
	// candidate destination, which would 404 on a non-candidate id.
	const desLink = linkTo || (useForJobApplicant ? `${PATHS.recruiter.job_applicants}/${jobApplicant?.id}` : candidateDetailPath(data?.id))

	const [copied, setCopied] = useState(false)
	const [isEmailHovered, setIsEmailHovered] = useState(false)
	const [tooltipOpen, setTooltipOpen] = useState(false)
	const timeoutRef = useRef()
	const tooltipId = useRef(0)
	const anchorRef = useRef(null)
	const nameRef = useRef(null)
	const emailRef = useRef(null)
	const enterTimer = useRef()
	const hovering = useRef(false)
	if (!tooltipId.current) tooltipId.current = ++tooltipSerial

	useEffect(() => {
		installTooltipScrollClose()
		const notify = (id) => setTooltipOpen(id === tooltipId.current)
		tooltipSubscribers.add(notify)
		return () => {
			tooltipSubscribers.delete(notify)
			clearTimeout(enterTimer.current)
			if (openTooltipId === tooltipId.current) publishTooltip(0)
		}
	}, [])

	// A row that is still mounted can be handed a different person. Drop the
	// chip that described the previous one.
	const personKey = `${data?.id || ''}\0${data?.fullName || ''}\0${data?.email || ''}`
	const personKeyRef = useRef(personKey)
	useEffect(() => {
		if (personKeyRef.current === personKey) return
		personKeyRef.current = personKey
		hovering.current = false
		clearTimeout(enterTimer.current)
		if (openTooltipId === tooltipId.current) publishTooltip(0)
	}, [personKey])

	useEffect(() => {
		if (!tooltipOpen) return undefined
		const onMove = (event) => {
			const anchor = anchorRef.current
			if (anchor && anchor.contains(event.target)) return
			hovering.current = false
			clearTimeout(enterTimer.current)
			if (openTooltipId === tooltipId.current) publishTooltip(0)
		}
		document.addEventListener('mousemove', onMove)
		return () => document.removeEventListener('mousemove', onMove)
	}, [tooltipOpen])

	const scheduleTooltip = () => {
		clearTimeout(enterTimer.current)
		hovering.current = true
		// Entering this cell means the pointer has left the previous one, even
		// when that row never received mouseleave. Bump the generation so a
		// timer that row already started cannot open afterwards.
		const generation = ++tooltipGeneration
		if (openTooltipId && openTooltipId !== tooltipId.current) publishTooltip(0)
		enterTimer.current = setTimeout(() => {
			if (generation !== tooltipGeneration || !hovering.current) return
			if (overflows(nameRef.current) || overflows(emailRef.current)) publishTooltip(tooltipId.current)
		}, TOOLTIP_DELAY)
	}

	const hideTooltip = () => {
		hovering.current = false
		clearTimeout(enterTimer.current)
		if (openTooltipId === tooltipId.current) publishTooltip(0)
	}

	const handleCopyEmail = async (e) => {
		e.stopPropagation()
		e.preventDefault()
		if (!data?.email) return

		try {
			await navigator.clipboard.writeText(data.email)
			setCopied(true)
			clearTimeout(timeoutRef.current)
			timeoutRef.current = setTimeout(() => {
				setCopied(false)
			}, 1000)
		} catch (err) {
			console.error('Failed to copy email: ', err)
		}
	}

	const EmailWithCopy = ({ children }) => (
		<div className={classes.emailContainer} onMouseEnter={() => setIsEmailHovered(true)} onMouseLeave={() => setIsEmailHovered(false)}>
			<div ref={emailRef} className={classes.email}>
				{children}
			</div>
			{data?.email && (
				<Fade in={isEmailHovered || copied} timeout={280}>
					<span style={{ flexShrink: 0 }}>
						{' '}
						{/* Prevent copy button from shrinking */}
						<Tooltip title={copied ? t('COPIED') : t('COPY')} placement="top" arrow>
							<IconButton
								size="small"
								onClick={handleCopyEmail}
								style={{
									padding: '2px',
									marginLeft: '-2px',
									color: copied ? '#388e3c' : isEmailHovered ? '#1976d2' : '#bdbdbd',
									transition: 'color 0.5s',
									display: 'flex',
									alignItems: 'center',
									background: 'none',
								}}
							>
								{copied ? <Check fontSize="small" style={{ width: 14, height: 14 }} /> : <CopySvg style={{ width: 14, height: 14 }} />}
							</IconButton>
						</Tooltip>
					</span>
				</Fade>
			)}
		</div>
	)

	return (
		<WrapperBox onClick={() => onClick?.()} {...rest}>
			<UserAvatar customWidth={32} customHeight={32} fontSize={16} firstName={data?.firstName} lastName={data?.lastName} src={data?.avatar?.file?.fileUrl} />
			{/*
			 * Opens only when the name or the email is actually cut off. A cell that
			 * already shows both has nothing to reveal, and the old tooltip repeated
			 * them on top of the next row. `top-start` keeps a real overflow chip
			 * off that next row. Hover listeners stay off: open state is shared, so
			 * entering another cell or scrolling closes whatever was left behind.
			 */}
			<Tooltip
				title={
					<div>
						<div>{data?.fullName}</div>
						<div>{data?.email}</div>
					</div>
				}
				placement="top-start"
				open={tooltipOpen}
				disableFocusListener
				disableHoverListener
				disableTouchListener
				TransitionProps={{ timeout: 0 }}
			>
				{disableClick ? (
					<div
						ref={anchorRef}
						className={`${classes.title} ${classes.person}`}
						style={{ color: theme.palette.primary.main, marginLeft: 10 }}
						onMouseEnter={scheduleTooltip}
						onMouseLeave={hideTooltip}
						onFocus={scheduleTooltip}
						onBlur={hideTooltip}
					>
						<div ref={nameRef} className={classes.fullName}>
							{data?.fullName}
						</div>
						<EmailWithCopy>{data?.email}</EmailWithCopy>
					</div>
				) : (
					<TitleLink
						ref={anchorRef}
						className={classes.title}
						color={color}
						disableClick={disableClick}
						onMouseEnter={scheduleTooltip}
						onMouseLeave={hideTooltip}
						onFocus={scheduleTooltip}
						onBlur={hideTooltip}
						// Clicking the name should ONLY navigate — stop the click from bubbling
						// to the table row's onRowClick (which opens the matched-jobs popup).
						// Otherwise the popup state gets set on the singleton store and re-appears
						// when navigating back to the matches list.
						onClick={(e) => e.stopPropagation()}
						to={{
							pathname: desLink,
							state: { from: { pathname: location.pathname, search: location.search } },
						}}
					>
						<div ref={nameRef} className={classes.fullName}>
							{data?.fullName}
						</div>
						<EmailWithCopy>{data?.email}</EmailWithCopy>
					</TitleLink>
				)}
			</Tooltip>
		</WrapperBox>
	)
}
